import type { ApiErrorBody } from '@amzjp/shared';

/**
 * §20.1 — API istemcisi.
 *
 * Access token bellekte tutulur (localStorage DEĞİL — XSS ile çalınmasın).
 * Refresh token httpOnly cookie'dedir; JS erişemez.
 * 401 alınca bir kez sessizce yenilenir; yine olmazsa oturum kapatılır.
 */

const BASE = '/api/v1';

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;
/** Aynı anda birden çok istek 401 alırsa tek yenileme yapılsın. */
let refreshPromise: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/** Sunucudan gelen yapılandırılmış hata. Mesajı doğrudan kullanıcıya gösterilebilir (Türkçe). */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
    readonly traceId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody | undefined;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    // Gövde JSON değilse aşağıdaki genel mesaj kullanılır
  }
  if (body?.error) {
    return new ApiError(
      body.error.code,
      body.error.message,
      response.status,
      body.error.details,
      body.error.traceId,
    );
  }
  return new ApiError(
    'NETWORK_ERROR',
    'Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.',
    response.status,
  );
}

async function tryRefresh(): Promise<boolean> {
  refreshPromise ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) return false;
      const data = (await response.json()) as { accessToken: string };
      accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Paylaşılan söz YALNIZCA eşzamanlı yenilemeleri tekilleştirmek içindir.
      // Sonuçlandığı anda bırakılmalı; aksi halde sonraki bir 401, çözülmüş
      // eski sonucu yeniden kullanıp gerçekten yenileme yapmaz.
      // (`finally`, bekleyenler devam etmeden önce çalışır; onlar söze zaten sahiptir.)
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

/** "Yeni jeton al": oturumu yeniler ve taze erişim jetonunu döner. Yenilenemezse `null`. */
export async function erisimJetonunuYenile(): Promise<string | null> {
  return (await tryRefresh()) ? accessToken : null;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Yenileme döngüsüne girmemek için iç kullanım. */
  skipRefresh?: boolean;
}

/**
 * Ham istek: 401'de bir kez sessizce yeniler, gövdeyi ayrıştırmaz.
 *
 * JSON uçları `apiFetch`, dosya indirmeleri `downloadFile` üzerinden bunu kullanır —
 * böylece token yenileme mantığı tek yerde kalır.
 */
export async function apiFetchRaw(path: string, options: RequestOptions = {}): Promise<Response> {
  const { body, skipRefresh, headers, ...rest } = options;

  const doFetch = async (): Promise<Response> =>
    fetch(`${BASE}${path}`, {
      ...rest,
      credentials: 'include',
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

  let response: Response;
  try {
    response = await doFetch();
  } catch {
    throw new ApiError(
      'NETWORK_ERROR',
      'Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.',
      0,
    );
  }

  if (response.status === 401 && !skipRefresh) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      response = await doFetch();
    } else {
      accessToken = null;
      onUnauthorized?.();
      throw await toApiError(response);
    }
  }

  return response;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await apiFetchRaw(path, options);

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
}

/**
 * §22.2 — Sunucudan akıtılan dosyayı indirir.
 *
 * `api.post` JSON bekler; burada yanıt gövdesi bir `.xlsx` akışı. Token bellekte
 * olduğu için normal `<a href>` ile indirilemez — isteği biz atıp gelen blob'u
 * geçici bir bağlantıyla kaydettiriyoruz.
 */
export async function downloadFile(
  path: string,
  body: unknown,
  fallbackName: string,
  /** Gövde göndermeyen indirmeler için GET. */
  method: 'GET' | 'POST' = body === undefined ? 'GET' : 'POST',
): Promise<void> {
  const response = await apiFetchRaw(path, {
    method,
    ...(method === 'POST' ? { body } : {}),
  });

  if (!response.ok) throw await toApiError(response);

  // Sunucunun verdiği dosya adını kullan; yoksa çağıranın önerdiğini
  const disposition = response.headers.get('content-disposition') ?? '';
  // `filename` yalnız ASCII taşıyor (Türkçe harfler `_` oluyor); gerçek ad varsa `filename*`ta
  const utf8Ad = /filename\*=utf-8''([^;\s]+)/i.exec(disposition)?.[1];
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = utf8Ad ? decodeURIComponent(utf8Ad) : (match?.[1] ?? fallbackName);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Tarayıcının indirmeyi başlatmasına fırsat ver, sonra belleği bırak
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  /*
   * DELETE gövdeyle çağrılabiliyor: toplu silmede yüzlerce kimliği URL'e
   * sığdırmak mümkün değil ve sorgu dizisi erişim loglarına düşer.
   */
  delete: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'DELETE', body }),
};
